import { useMemo, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAtomValue } from 'jotai';
import { isEmpty } from 'lodash';
import { currentMetricsMetaSelector } from '@store/common/selectors';
import { dimensionByTypesSelector } from '@store/common/dimensionList/selectors';
import {
  formatColumns,
  formatAppData,
  formatMultiColumns,
  formatMultiData,
  SORT_TYPES,
} from '../../../utils';
import { getMetricByUnit } from '@utils/metrics/metricHelpers';
import useFetchGroupBy from '../../../../hooks/useFetchGroupBy';
import { useAppCommonQueryParams } from '../../../../hooks/useAppDimension';
import { errorDataAtom } from '@src/components/metrics-panel-hz/atoms';
import { useDrilldownMeta } from '@src/hooks/common/useDrilldownMeta';

const getUnknownName = (dataItem: Record<string, any>) => {
  if (Array.isArray(dataItem.name)) {
    return dataItem.name[0];
  }
  return dataItem.name;
};

export const useAppMultiDimensions = ({ dimensions, unit, enabled = true }) => {
  const currentMetricsMeta = useSelector(currentMetricsMetaSelector);
  const { flowId, frustrationSignalId }: any = useDrilldownMeta();
  const errorData = useAtomValue(errorDataAtom);
  const {
    queryMetrics: metrics,
    filter,
    interval,
    metricFilter,
    sortByMetric,
    limit,
    realTimeOnTrends,
    noBrushSelected,
    selectedMetric,
  }: any = useAppCommonQueryParams();

  const dimensionByTypes: any = useSelector(dimensionByTypesSelector);

  const [sortBy, setSortBy] = useState({ key: sortByMetric, order: 'desc' as const });

  // update local sort when selected or order by metric changes
  useEffect(() => {
    setSortBy({ key: sortByMetric, order: 'desc' });
  }, [sortByMetric, selectedMetric]);

  const {
    isFetching,
    isError,
    data = {},
    refetch,
  } = useFetchGroupBy({
    dimension: dimensions,
    metrics,
    filter,
    interval,
    enabled,
    metricFilter,
    sortByMetric,
    limit,
    realTimeOnTrends,
    noBrushSelected,
    withTotals: false,
  });

  const { adjustedInterval }: any = data;

  const { rows, totals, columns, pctShareMaxValues } = useMemo(() => {
    let rows = [];
    let totals = {};
    let columns = [];
    let pctShareMaxValues = {};

    // unit based metrics
    const columnMetrics = metrics.map((metric) => {
      return getMetricByUnit(unit, metric);
    });

    if (!isEmpty(dimensionByTypes)) {
      // Need to test metric filter data case
      if ('metricFilterData' in data) {
        ({ rows, totals, pctShareMaxValues } = formatMultiData({
          data,
          sortBy: {
            ...sortBy,
            type: SORT_TYPES.metric,
          },
          metrics: columnMetrics,
          getUnknownName,
        }));
        columns = formatMultiColumns({
          dimension: dimensions,
          metrics: columnMetrics,
          showNAForMetric: null,
          showActionColumn: true,
          showTrend: false,
          errorData,
          flowId, // Use null to avoid type issues
          frustrationSignalId,
        });
      } else {
        // Format data without the flowId parameter as it's not part of the expected props
        ({ rows, totals, pctShareMaxValues } = formatAppData({
          data,
          sortBy: {
            ...sortBy,
            type: SORT_TYPES.metric,
          },
          getUnknownName,
          flowId,
          frustrationSignalId,
        }));
        columns = formatColumns({
          dimension: dimensions,
          metrics: columnMetrics,
          showNAForMetric: null,
          errorData,
          disableDrilldown: false,
          showHeaderTooltip: false,
          flowId, // Use null to avoid type issues
          frustrationSignalId,
        });
      }
    }

    return { rows, totals, columns, pctShareMaxValues };
  }, [
    data,
    dimensions,
    dimensionByTypes,
    metrics,
    currentMetricsMeta,
    realTimeOnTrends,
    sortBy,
    unit,
    errorData,
  ]);

  return {
    isFetching,
    isError,
    refetch,
    columns,
    isEmpty: rows.length === 0,
    totals,
    data: rows,
    sortBy,
    adjustedInterval,
    pctShareMaxValues,
    metrics,
  };
};
